import React, { useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faComment, faTag, faCheck, faChessBoard } from '@fortawesome/free-solid-svg-icons';
import { GameMoveModel, GameMovePairModel } from '../types/GamesModel';
import GamesMove from './GamesMove';
import GamesMoveTypes from './GamesMoveTypes';
import { useAppDispatch } from '../store';
import AppContext from '../context/AppContext';
import GameContext from '../context/GameContext';
import AnnotationsContext from '../context/AnnotationsContext';
import UserTagsContext from '../context/UserTagsContext';
import { setParams } from '../reducers/paramsReducer';
import { useCorrections } from './CorrectionsProvider';
import { usePractice } from './PracticeProvider';
import { cursorTipProps } from '../utils/cursorTip';

// The repair badge's hover text: an invitation before the first repair, a tally after.
function repairTip(repairCount: number, solved: boolean): string {
  if (repairCount === 1) return 'Repaired once. Repair it again';
  if (repairCount === 2) return 'Repaired twice. Repair it again';
  if (repairCount > 2) return `Repaired ${repairCount} times. Repair it again`;
  // Solved but no count yet: don't invite a "first" repair next to a green check.
  if (solved) return 'Repaired. Repair it again';
  return 'Repair this move';
}

// A single move cell. Clicking walks the board to the move, which retargets the docked comment panel
// via GameContext.currentMove; the current move is highlighted by .yellow (see GamesMove). The star
// shows a favourite; the comment icon opens the move's thread (the bottom sheet on mobile).
function RenderMoveColumn(props: { move: GameMoveModel }): React.ReactNode {
  const { move } = props;
  const { isMobile } = useContext(AppContext);
  const { chessviewerRef } = useContext(GameContext);
  const { favorites, comments, requestComposerFocus } = useContext(AnnotationsContext);
  const { moveTags } = useContext(UserTagsContext);
  const { activeMistakes } = useCorrections();
  const practice = usePractice();
  const dispatch = useAppDispatch();

  const isFav = favorites.has(move.Pgn);
  const count = comments.filter((c) => c.MovePgn === move.Pgn && c.Status !== 'Removed').length;
  const tagCount = moveTags[move.Pgn]?.length ?? 0;
  // This move as a correctable mistake in a selected category, for the practice badge.
  const mistake = move.MoveId ? activeMistakes.find((m) => m.MoveId === move.MoveId) : undefined;

  const goToBoard = () => {
    const chessviewer = chessviewerRef.current;
    if (chessviewer) {
      chessviewer.go(move.Pgn);
    } else {
      dispatch(setParams({ pgn: move.Pgn }));
    }
    // On mobile the board sits above the list, so bring it back into view.
    if (isMobile) {
      document
        .querySelector('.cm-mobile-board')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // The comment icon jumps to the move and focuses the comment box (scrolling to the board on mobile).
  const openComments = (e: React.MouseEvent) => {
    e.stopPropagation();
    requestComposerFocus(move.Pgn);
    goToBoard();
  };

  // The tag badge walks the board to the move; its tag picker is in the docked comment panel.
  const openTags = (e: React.MouseEvent) => {
    e.stopPropagation();
    goToBoard();
  };

  // The practice badge goes straight into correcting this mistake, switching if already correcting.
  const openCorrect = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (mistake) practice.select(mistake.MoveId);
  };

  return (
    <td className="cm-move cm-move-clickable">
      <div className="d-flex flex-row align-items-center" onClick={goToBoard}>
        <GamesMoveTypes
          showList={false}
          move={move}
          moveType={move.MoveType ?? undefined}
          setMoveType={() => {}}
        ></GamesMoveTypes>
        <GamesMove move={move} showAlternatives={false}></GamesMove>
        <span className="cm-move-badges ms-auto d-flex align-items-center">
          {mistake ? (
            <button
              type="button"
              className={`cm-move-badge-correct${
                mistake.effectiveSolved ? ' is-solved' : ''
              }${practice.active && practice.selected?.MoveId === mistake.MoveId ? ' is-active' : ''}`}
              onClick={openCorrect}
              {...cursorTipProps(repairTip(mistake.effectiveRepairCount, mistake.effectiveSolved))}
              aria-label={repairTip(mistake.effectiveRepairCount, mistake.effectiveSolved)}
            >
              <FontAwesomeIcon icon={mistake.effectiveSolved ? faCheck : faChessBoard} />
            </button>
          ) : null}
          {isFav ? <FontAwesomeIcon icon={faStar} className="cm-move-badge-star" /> : null}
          {tagCount > 0 ? (
            <button
              type="button"
              className="cm-move-badge-tags"
              onClick={openTags}
              {...cursorTipProps(`${tagCount} tag${tagCount === 1 ? '' : 's'} on this move`)}
              aria-label="See your tags for this move"
            >
              <FontAwesomeIcon icon={faTag} />
              {tagCount > 1 ? <span className="cm-move-badge-count">{tagCount}</span> : null}
            </button>
          ) : null}
          <button
            type="button"
            className={`cm-move-badge-comments${count > 0 ? ' has-comments' : ''}`}
            onClick={openComments}
            {...cursorTipProps(
              count > 0 ? `${count} comment${count === 1 ? '' : 's'}` : 'Add a comment',
            )}
            aria-label="Open comments for this move"
          >
            <FontAwesomeIcon icon={faComment} />
            {count > 0 ? <span className="cm-move-badge-count">{count}</span> : null}
          </button>
        </span>
      </div>
    </td>
  );
}

function GamesListItemEditorMovePair(props: { movePair: GameMovePairModel }): React.ReactNode {
  const { movePair } = props;

  return (
    <tr>
      <td>
        <span>{movePair.Number}.</span>
      </td>
      <RenderMoveColumn move={movePair.White} />
      {movePair.Black != null ? <RenderMoveColumn move={movePair.Black} /> : <td></td>}
    </tr>
  );
}

export default function GamesListItemEditor(): React.ReactNode {
  const { game } = useContext(GameContext);

  return (
    <div className="flex-fill">
      <table className="table table-sm table-striped cm-moves-table">
        <thead>
          <tr>
            <th scope="col" style={{ width: 20 }}>
              #
            </th>
            <th scope="col">White</th>
            <th scope="col">Black</th>
          </tr>
        </thead>
        <tbody>
          {game.Moves.map((movePair, i) => {
            return <GamesListItemEditorMovePair key={i} movePair={movePair} />;
          })}
        </tbody>
      </table>
    </div>
  );
}
